import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useMe } from '../hooks/useCustomer';
import { useCart } from '../hooks/useCart';
import { apiCall } from '../lib/api';
import { ShopIcon, CartIcon, PackageIcon, TicketIcon, GiftIcon, ArrowRightIcon, CrownIcon } from '../components/icons';

interface PointsSummary {
  points: number;
  tier: string;
  progress: {
    current_tier: string; next_tier: string | null;
    points_to_next: number; pct: number;
  };
}

// Per Line CRM Feedback: "ส่วนทีเป็นสีแดง เปลี่ยนเป็นสีดำของ mesoestetic"
const TIER_GRADIENT: Record<string, string> = {
  Crystal:  'from-gray-800 to-black',
  Silver:   'from-gray-400 to-gray-700',
  Gold:     'from-amber-500 to-yellow-700',
  Platinum: 'from-slate-700 to-slate-900',
};

export function Dashboard() {
  const { data: customer, isLoading } = useMe();
  const cart = useCart();
  const points = useQuery<PointsSummary>({
    queryKey: ['points', 'me', 'summary'],
    queryFn: () => apiCall<PointsSummary>('get', '/customers/me/points'),
    staleTime: 60_000,
  });

  if (isLoading) return <div className="p-6 text-center text-gray-500">กำลังโหลด...</div>;
  if (!customer) return <div className="p-6 text-center text-gray-500">ไม่พบข้อมูล</div>;

  const tierGrad = TIER_GRADIENT[points.data?.progress.current_tier ?? customer.tier] ?? TIER_GRADIENT.Crystal;

  return (
    <div className="max-w-md mx-auto px-4 py-4 space-y-3">
      {/* Brand header */}
      <div className="flex items-center justify-center py-2">
        <div className="text-xl font-bold tracking-tight text-black">mesoestetic</div>
      </div>

      {/* Hero card — clickable → /points */}
      <Link to="/points" className={`card block bg-gradient-to-br ${tierGrad} text-white active:scale-[0.99] transition`}>
        <div className="flex items-center gap-3">
          {customer.line_picture_url && (
            <img src={customer.line_picture_url}
                 className="w-12 h-12 rounded-full border-2 border-white/40" alt="" />
          )}
          <div className="flex-1 min-w-0">
            <div className="font-bold text-lg">สวัสดี {customer.first_name}</div>
            <div className="text-xs text-white/70 flex items-center gap-1">
              <CrownIcon className="w-3.5 h-3.5" />
              <span>{customer.tier} Member</span>
            </div>
          </div>
          <ArrowRightIcon className="w-4 h-4 text-white/70" />
        </div>
        <div className="mt-4">
          <div className="text-xs text-white/70">คะแนนสะสม</div>
          <div className="text-3xl font-bold">{(points.data?.points ?? customer.points).toLocaleString('th-TH')} <span className="text-base font-semibold opacity-80">คะแนน</span></div>
        </div>

        {/* Progress bar to next tier */}
        {points.data?.progress.next_tier && (
          <div className="mt-3">
            <div className="flex justify-between text-[11px] mb-1 opacity-90">
              <span>{points.data.progress.current_tier}</span>
              <span>{points.data.progress.next_tier}</span>
            </div>
            <div className="h-1.5 bg-white/20 rounded-full overflow-hidden">
              <div className="h-full bg-white rounded-full transition-all"
                   style={{ width: `${points.data.progress.pct}%` }} />
            </div>
            <div className="text-[11px] mt-1 opacity-90">
              อีก {points.data.progress.points_to_next.toLocaleString('th-TH')} คะแนน → {points.data.progress.next_tier}
            </div>
          </div>
        )}
      </Link>

      {/* Cart card */}
      {cart.count > 0 ? (
        <Link to="/shop/cart"
              className="card flex items-center justify-between bg-gradient-to-r from-emerald-500 to-teal-600 text-white">
          <div className="flex items-center gap-3">
            <CartIcon className="w-7 h-7" />
            <div>
              <div className="font-bold">ตะกร้าสินค้า ({cart.count} ชิ้น)</div>
              <div className="text-xs opacity-80">
                ฿{cart.total.toLocaleString('th-TH', { minimumFractionDigits: 2 })}
              </div>
            </div>
          </div>
          <ArrowRightIcon className="w-5 h-5 text-white/80" />
        </Link>
      ) : (
        <Link to="/shop/cart" className="card flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CartIcon className="w-6 h-6 text-gray-700" />
            <div className="font-semibold text-sm">ตะกร้าสินค้า</div>
          </div>
          <span className="text-xs text-gray-400">ว่างเปล่า</span>
        </Link>
      )}

      {/* Action grid */}
      <div className="grid grid-cols-2 gap-2">
        <Link to="/shop" className="card text-center">
          <ShopIcon className="w-8 h-8 mx-auto text-gray-700" />
          <div className="text-sm font-semibold mt-1">ช้อปสินค้า</div>
        </Link>
        <Link to="/orders" className="card text-center">
          <PackageIcon className="w-8 h-8 mx-auto text-gray-700" />
          <div className="text-sm font-semibold mt-1">คำสั่งซื้อ</div>
        </Link>
        <Link to="/coupons" className="card text-center">
          <TicketIcon className="w-8 h-8 mx-auto text-gray-700" />
          <div className="text-sm font-semibold mt-1">รับคูปอง</div>
        </Link>
        <Link to="/rewards" className="card text-center">
          <GiftIcon className="w-8 h-8 mx-auto text-gray-700" />
          <div className="text-sm font-semibold mt-1">แลกรางวัล</div>
        </Link>
      </div>

      {/* Referral */}
      <div className="card">
        <div className="text-sm text-gray-500">รหัสแนะนำของคุณ</div>
        <div className="text-lg font-bold">{customer.referral_code}</div>
        <div className="text-xs text-gray-400 mt-1">
          แชร์ลิงก์ให้เพื่อน — ลูกค้าจะได้รับข้อเสนอพิเศษ
        </div>
      </div>

      {/* Chat with mesoestetic via LINE */}
      <a
        href="https://lin.ee/UkwHnGT"
        target="_blank"
        rel="noopener noreferrer"
        className="block w-full bg-[#06C755] text-white rounded-xl py-3.5 px-5
                   flex items-center justify-center gap-2.5
                   font-semibold text-base shadow-md active:scale-[0.99] transition"
        style={{ boxShadow: '0 2px 6px rgba(6, 199, 85, 0.25)' }}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M12 2C6.486 2 2 5.682 2 10.211c0 4.06 3.557 7.466 8.359 8.108.327.071.766.215.879.495.099.255.064.652.031.91l-.143.857c-.044.255-.205.998.875.544 1.081-.453 5.831-3.434 7.957-5.881 1.466-1.609 2.171-3.245 2.171-5.033C22 5.682 17.514 2 12 2zM7.95 13.247a.196.196 0 0 1-.197.196H5.345a.196.196 0 0 1-.197-.196V8.875c0-.108.088-.196.197-.196H6.04c.109 0 .196.088.196.196v3.582h1.518c.108 0 .196.088.196.196v.594zm1.683 0a.197.197 0 0 1-.197.196h-.694a.197.197 0 0 1-.197-.196V8.875c0-.108.088-.196.197-.196h.694c.108 0 .197.088.197.196v4.372zm4.847 0a.197.197 0 0 1-.197.196h-.737a.205.205 0 0 1-.157-.077l-2.005-2.706v2.587a.197.197 0 0 1-.196.196h-.695a.197.197 0 0 1-.197-.196V8.875c0-.108.088-.196.197-.196h.738a.198.198 0 0 1 .157.078l1.998 2.696V8.875c0-.108.088-.196.196-.196h.695c.108 0 .197.088.197.196v4.372zm3.918-3.78a.197.197 0 0 1-.197.196h-1.519v.586h1.519c.108 0 .197.088.197.196v.594a.197.197 0 0 1-.197.197h-1.519v.587h1.519c.108 0 .197.088.197.196v.594a.197.197 0 0 1-.197.196h-2.408a.197.197 0 0 1-.196-.196V8.875c0-.108.088-.196.196-.196h2.408c.108 0 .197.088.197.196v.592z"/>
        </svg>
        แชทกับเราผ่าน LINE
      </a>
    </div>
  );
}
